import {Injectable} from '@angular/core';
import {BehaviorSubject, map, Observable, Subject, switchMap, take, throwError, zip,} from 'rxjs';
import {
  Currencies,
  PriceList,
  SimpleOrder,
  SimpleOrderEntry,
  SimpleOrderEntryMetadata,
  VariantBase,
  VariantPrice
} from "../model/products";
import {environment} from "../../../environments/environment";
import {AuthorizationServiceProvider} from "../../auth_profile/services/authorization-service.provider";
import {ToastNotification, ToastNotificationsService} from "../../utils/services/toast-notification.service";
import {StudentCheckoutRequestInput} from '../../auth_profile/model/v3/transactions';
import {appendIfDefined} from "../../auth_profile/services/api/v3/rest-v3-utils";
import {HttpClient, HttpParams} from "@angular/common/http";
import {NativeServiceApiProvider} from "../../services/native-api-provider.service";
import * as LessonsV3 from '../model/v3/lessons';
import { CartProductV3, CartV3 } from '../model/v3/shopping-cart';
import { Pageable } from '../../utils/pageable';

@Injectable({
  providedIn: 'root'
})
export class StudentShoppingCartService {

  orderComplete = new Subject<any>();
  isCartHidden = new BehaviorSubject<boolean>(true)
  private currency = new BehaviorSubject<string>(Currencies.values[0]);
  private cart: CartV3 = { items: [], total: 0 };
  private cartState = new BehaviorSubject<CartV3>(this.cart)
  private products: LessonsV3.ProductVariantPreview[];
  private locale: string
  private studentId: number
  private schoolId: number
  private initialized = false;

  constructor(
    private apiProvider: NativeServiceApiProvider,
    private http: HttpClient,
    private authService: AuthorizationServiceProvider,
    private toastNotificationService: ToastNotificationsService
  ) {

    this.authService.getAuthDetailsService().pipe(
      switchMap(service => zip(service.getSelfStudentId(), service.getSelfSchoolId()))
    ).subscribe(([studentId, schoolId]) => {
      this.studentId = studentId
      this.schoolId = schoolId
      this.initializeCart()
    })
  }

  initializeCart() {
    if (this.initialized) return;
    this.initialized = true;
    if(localStorage.getItem("cart")) {
      this.cart = JSON.parse(localStorage.getItem("cart"))
      this.cartState.next(this.cart)
    }
    if(localStorage.getItem("currency")) {
      this.setCurrency(localStorage.getItem("currency"))
    }
  }

  openCart() {
    this.isCartHidden.next(false)
  }

  closeCart() {
    this.isCartHidden.next(true)
  }

  getCart(): Observable<CartV3> {
    return this.cartState
  }

  setCurrency(currency: string) {
    localStorage.setItem("currency", currency)
    this.currency.next(currency)
    if (!this.locale) {
      this.locale = 'en';
    }
    this.apiProvider
      .colV3()
      .pipe(
        switchMap((api) =>
          api.getProductVariants(
            'preview',
            'snipcart',
            null,
            this.locale,
            this.currency.getValue(),
            ['regular'],
            new Pageable(0, 500, null)
          )
        )
      )
      .subscribe((page) => {
        this.products = page?.content ?? []
        this.updatePrices()
        this.updateTotal()
        this.updateCart()
      })
  }

  getCurrency(): Observable<string> {
    return this.currency.pipe(
      take(1)
    )
  }

  getCurrencySign(): string {
    const currency = this.currency.getValue()
    if(currency === 'eur')
      return '€'
    else if(currency === 'pln')
      return 'zł'
    else if(currency === 'usd')
      return '$'
    else if(currency === 'gbp')
      return '£'
    else if(currency === 'jpy')
      return '¥'
    return ''
  }

  addItem(product: LessonsV3.ProductVariantPreview) {
    const existing = this.cart.items.find((variant) => variant.product.code === product.code);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.cart.items.push({ product, quantity: 1 });
    }
    this.updateTotal()
    this.updateCart()
    this.openCart()
  }

  updateCart() {
    this.cartState.next(this.cart)
    localStorage.setItem("cart", JSON.stringify(this.cart))
  }

  increment(_product: CartProductV3) {
    const product = this.cart.items.find(variant => variant.product.code === _product.product.code);
    if (!product) {
      return;
    }
    product.quantity += 1;
    this.updateTotal()
    this.updateCart()
  }

  decrement(_product: CartProductV3) {
    const product = this.cart.items.find(variant => variant.product.code === _product.product.code)
    if (!product) {
      return;
    }
    product.quantity > 1?
      product.quantity -=1 :
      this.delete(_product)
    this.updateTotal()
    this.updateCart()
  }

  delete(product: CartProductV3) {
    this.cart.items.splice(this.cart.items.indexOf(product,0),1)
    this.updateTotal()
    this.updateCart()
  }

  deleteCart() {
    localStorage.removeItem("cart")
    this.cart = { items: [], total: 0 }
    this.updateTotal()
    this.updateCart()
  }

  updateTotal() {
    let total = 0
    this.cart.items.forEach( el =>
    { return total += (el.product.price?.value ?? 0) * el.quantity})
    this.cart.total = total
  }

  updatePrices() {
    this.cart.items.map(item => {
      const latest = this.products?.find(product => item.product.code === product.code);
      if (latest?.price) {
        item.product.price = latest.price;
      }
      return item
    })
  }

  checkout() {
    this.changeOrderStatus()
    const order = new SimpleOrder()
    order.entries = new Array<SimpleOrderEntry>()

    this.cart.items.forEach(item => {
      const mappedItem = new SimpleOrderEntry()
      mappedItem.price = new VariantPrice()
      mappedItem.price.priceList = new PriceList()
      mappedItem.price.variant = new VariantBase()
      mappedItem.metadata = new SimpleOrderEntryMetadata()
      mappedItem.price.currency = localStorage.getItem("currency") ? localStorage.getItem("currency") : (item.product.price?.currency ?? '')
      mappedItem.price.priceList.name = item.product.price?.priceList?.name ?? 'regular'
      mappedItem.price.value = item.product.price?.value ?? 0
      mappedItem.price.variant.code = item.product.code
      mappedItem.quantity = item.quantity
      mappedItem.metadata.quantityAdjustable = true
      order.entries.push(mappedItem)
    })

    this.sendOrder(order).subscribe({
      next: res => {
        this.closeCart()
        this.deleteCart()
        this.changeOrderStatus()
        window.location.href = res;
      },
      error: err => {
        this.closeCart()
        this.toastNotificationService.display(new ToastNotification("Communication Error", `${err.userMessage}`))
      }
    })
  }

  sendOrder(order: SimpleOrder): Observable<string> {
    if (!this.studentId || !this.schoolId) {
      return throwError(() => new Error('Missing checkout context: studentId or schoolId'));
    }

    const successUrl = `${environment.serverBase}/order-success`
    const cancelUrl = `${environment.serverBase}/order-fail`
    const request = this.mapToStudentCheckoutRequest(order)

    return this.createStudentCheckout(
      null,
      successUrl,
      cancelUrl,
      cancelUrl,
      request
    )
  }

  createStudentCheckout(
    platform: string | null,
    successUrl: string | null,
    errorUrl: string | null,
    cancelUrl: string | null,
    body: StudentCheckoutRequestInput,
  ): Observable<string> {
    const params = [
      ['type', 'student'],
      ['platform', platform],
      ['successUrl', successUrl],
      ['errorUrl', errorUrl],
      ['cancelUrl', cancelUrl],
    ].reduce(
      (acc, [key, value]) =>
        appendIfDefined(
          acc,
          key as string,
          value as string | number | boolean | null,
        ),
      new HttpParams(),
    );

    return this.http
      .post<void>(`${environment.eventsApiBase}/v1/checkouts`, body, {
        params,
        observe: 'response',
      })
      .pipe(map((res) => {
        return res.headers.get('Location')
      }));
  }

  changeOrderStatus() {
    this.orderComplete.next(null)
  }

  private mapToStudentCheckoutRequest(order: SimpleOrder): StudentCheckoutRequestInput {
    const entries = (order.entries || [])
      .filter(entry => !!entry?.quantity)
      .map(entry => ({
        product: {mappedId: entry.price?.variant?.code},
        amount: entry.quantity,
        itemPrice: entry.price?.value,
        currency: entry.price?.currency,
        description: null,
      }));

    return {
      school: {id: this.schoolId},
      student: {id: this.studentId},
      entries,
    };
  }
}
